import {
  DEAL_PARTNER_PROFIT_TYPE_OPTIONS,
  DEAL_TYPE_OPTIONS,
  PROFIT_RULE_OPTIONS,
  calculatedInvestmentRatio,
  hasShowroomCharge,
  investmentAmountTotal,
  showsOwnerDemandPrice,
  type DealPartnerDraft,
  type ProductDealType,
  type ProductProfitRuleType,
  type ShowroomChargeDraft,
} from "@/lib/catalog/dealPartners";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { cn } from "@/lib/utils";
import { Handshake, Loader2 } from "lucide-react";

export type ProductDealSummaryData = {
  dealType?: ProductDealType | null;
  profitRuleType?: ProductProfitRuleType | null;
  ownerDemandPrice?: number | null;
  expectedSalePrice?: number | null;
  landedCost?: number | null;
  salePrice?: number | null;
  dealPartners?: DealPartnerDraft[];
  showroomCharge?: ShowroomChargeDraft | null;
  /** @deprecated Use showroomCharge */
  companyOwnerCommission?: ShowroomChargeDraft | null;
};

type Props = {
  data: ProductDealSummaryData | null;
  loading?: boolean;
  className?: string;
};

function optionLabel<T extends string>(
  options: { value: T; label: string }[],
  value: T | null | undefined,
): string | null {
  if (value == null) return null;
  return options.find((o) => o.value === value)?.label ?? value;
}

function formatMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const value = Number(n);
  const formatted = formatCurrency(value);
  const short = pkrAmountShortLabel(value);
  return short ? `${formatted} · ${short}` : formatted;
}

function InlineStatRaw({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 shrink-0 space-y-0.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 leading-none">
        {label}
      </p>
      <p className="text-xs font-semibold tabular-nums text-foreground whitespace-nowrap">{value}</p>
    </div>
  );
}

function InlineStatMoney({
  label,
  amount,
  highlight = false,
  badgeVariant = "amber",
}: {
  label: string;
  amount: number | null | undefined;
  highlight?: boolean;
  badgeVariant?: "amber" | "primary";
}) {
  if (amount == null || !Number.isFinite(Number(amount))) {
    return <InlineStatRaw label={label} value="—" />;
  }

  const num = Number(amount);
  const formatted = formatCurrency(num);
  const short = pkrAmountShortLabel(num);

  return (
    <div className="min-w-0 shrink-0 space-y-0.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 leading-none">
        {label}
      </p>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            "text-xs font-semibold tabular-nums whitespace-nowrap",
            highlight ? "text-primary font-bold" : "text-foreground",
          )}
        >
          {formatted}
        </span>
        {short ? (
          <span
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.25 text-[10px] font-bold tracking-tight shrink-0 border",
              badgeVariant === "primary"
                ? "bg-primary/15 border-primary/30 text-primary"
                : "bg-amber-500/15 border-amber-500/30 text-amber-800 dark:text-amber-300",
            )}
          >
            {short}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function partnerProfitDisplay(row: DealPartnerDraft): string {
  const profitTypeLabel = optionLabel(DEAL_PARTNER_PROFIT_TYPE_OPTIONS, row.profitType);
  if (row.profitType === "percentage" && row.profitValue != null) {
    return `${Number(row.profitValue).toFixed(2)}%`;
  }
  if (row.profitType === "fixed" && row.profitValue != null) {
    return formatMoney(row.profitValue);
  }
  return profitTypeLabel ?? "—";
}

/** Read-only deal snapshot for sale forms — updates with the selected product. */
export function ProductDealSummary({ data, loading = false, className }: Props) {
  if (loading) {
    return (
      <div
        className={cn(
          "rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 flex items-center gap-3 text-sm text-muted-foreground",
          className,
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin text-amber-500 shrink-0" />
        Loading deal details…
      </div>
    );
  }

  if (!data) return null;

  const showroom = data.showroomCharge ?? data.companyOwnerCommission ?? null;
  const partners = data.dealPartners ?? [];
  const investmentTotal = investmentAmountTotal(partners);
  const dealTypeLabel = optionLabel(DEAL_TYPE_OPTIONS, data.dealType ?? null) ?? "Not set";
  const profitRuleLabel =
    data.dealType === "purchased"
      ? "Not applicable"
      : optionLabel(PROFIT_RULE_OPTIONS, data.profitRuleType ?? null) ?? "Not set";
  const showOwnerDemand = showsOwnerDemandPrice(data.profitRuleType);
  const saleAsk = data.expectedSalePrice ?? data.salePrice ?? null;
  const hasShowroom = hasShowroomCharge(showroom);
  const hasAnyDeal =
    data.dealType != null ||
    data.profitRuleType != null ||
    (showOwnerDemand && data.ownerDemandPrice != null) ||
    saleAsk != null ||
    hasShowroom ||
    partners.length > 0;

  const showroomChargeLabel =
    showroom?.commissionType === "sale_percentage" && showroom.commissionValue != null
      ? `${Number(showroom.commissionValue)}% of sale`
      : showroom?.commissionType === "cost_percentage" && showroom.commissionValue != null
        ? `${Number(showroom.commissionValue)}% of cost`
        : showroom?.commissionType === "profit_percentage" && showroom.commissionValue != null
          ? `${Number(showroom.commissionValue)}% of profit`
          : showroom?.commissionType === "fixed" && showroom.commissionValue != null
            ? formatMoney(showroom.commissionValue)
            : "—";

  const showroomCompany =
    showroom?.partyName?.trim() ||
    (showroom?.partyId ? `Party #${showroom.partyId}` : "—");

  return (
    <div
      className={cn(
        "rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.08] via-amber-500/[0.03] to-transparent p-4 shadow-sm backdrop-blur-sm transition-all",
        className,
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <Handshake className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                Product Deal Terms
              </h4>
              <span className="rounded-md border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.25 text-[10px] font-medium text-amber-800 dark:text-amber-300">
                Read-only
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">Pre-configured profit & ownership rules</p>
          </div>
        </div>

        {!hasAnyDeal ? (
          <p className="text-xs text-muted-foreground">No deal terms configured for this product.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-border/40 bg-background/60 px-3.5 py-2">
            <InlineStatRaw label="Deal type" value={dealTypeLabel} />
            <div className="h-4 w-px bg-border/60 hidden sm:block" />
            <InlineStatRaw label="Profit rule" value={profitRuleLabel} />
            {showOwnerDemand ? (
              <>
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <InlineStatMoney label="Owner demand" amount={data.ownerDemandPrice} badgeVariant="amber" />
              </>
            ) : null}
            {data.landedCost != null ? (
              <>
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <InlineStatMoney label="Landed cost" amount={data.landedCost} highlight badgeVariant="primary" />
              </>
            ) : null}
            {hasShowroom && data.profitRuleType !== "owner_surplus" && data.dealType !== "purchased" ? (
              <>
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <InlineStatRaw label="Showroom" value={showroomCompany} />
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <InlineStatRaw label="Charges" value={showroomChargeLabel} />
              </>
            ) : null}
            {partners.length > 0 ? (
              <>
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 leading-none">
                    Partners ({partners.length})
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {partners.map((row) => {
                      const ratio =
                        row.investmentRatio != null && Number.isFinite(row.investmentRatio)
                          ? row.investmentRatio
                          : calculatedInvestmentRatio(row.investmentAmount, investmentTotal);
                      const partyLabel =
                        row.partyName?.trim() ||
                        (row.partyId != null ? `Party #${row.partyId}` : "Unnamed");
                      const isOwnerOrPurchased = data.profitRuleType === "owner_surplus" || data.dealType === "purchased";
                      const ratioPart = isOwnerOrPurchased || ratio == null ? "" : ` · ${Number(ratio).toFixed(0)}%`;
                      const profitPart = isOwnerOrPurchased ? "" : ` · ${partnerProfitDisplay(row)}`;

                      return (
                        <span
                          key={row.key}
                          className="inline-flex max-w-[16rem] items-center rounded-md border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-xs text-foreground"
                          title={`${partyLabel}${ratioPart}${profitPart}`}
                        >
                          <span className="font-semibold truncate">{partyLabel}</span>
                          <span className="text-muted-foreground whitespace-nowrap">
                            {ratioPart}
                            {profitPart}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="h-4 w-px bg-border/60 hidden sm:block" />
                <InlineStatRaw label="Partners" value="None" />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
